/**
 * fr3Import — convert a FastReport .fr3 LABEL layout into the designer's
 * element model (JS port of the Android Fr3ToZplConverter mapping,
 * MyFasManMobile Fr3ToZplConverter.java).
 *
 * Coordinate model: FastReport design units are 1/96"; PaperWidth/PaperHeight
 * are millimetres. Element geometry converts to mm; font heights convert to
 * printer dots at the model's dpmm.
 *
 * The import targets the CLASSIC kasvar layout, whose print path substitutes
 * only [TITLE] [LINE1-4] [SIZE] [BARCODE] [PRICE] [DETAIL4] [SEASON]
 * [DECIMAL] (LabelPrinter.java:184-199). fr3 PrintQ fields are therefore
 * mapped: exact matches first (VP→PRICE, SEIZOEN→SEASON, …), every other
 * field takes the next free composite slot (TITLE, LINE1..LINE4) — the
 * properties panel lets the user re-pick afterwards. Unmappable fields stay
 * as-is and are reported in `warnings`.
 *
 * Returns { ok, model, warnings } or { ok:false, reason }.
 */

import { nextId } from './zplModel';

const FR_UNITS_PER_INCH = 96.0;

function unitsToMm(units) {
    return round2((units / FR_UNITS_PER_INCH) * 25.4);
}

function unitsToDots(units, dpi) {
    return Math.round((units * dpi) / FR_UNITS_PER_INCH);
}

function mmToUnits(mm) {
    return (mm / 25.4) * FR_UNITS_PER_INCH;
}

function round2(v) {
    return Math.round(v * 100) / 100;
}

/** FastReport numeric attribute, tolerating the Belgian comma decimal. */
function attrD(el, name, def) {
    const v = el.getAttribute(name);
    if (v === null || v === '') return def;
    const parsed = parseFloat(String(v).replace(',', '.'));
    return isNaN(parsed) ? def : parsed;
}

function attr(el, name, def) {
    const v = el.getAttribute(name);
    return v === null ? def : v;
}

// [DM1.PrintQ."field"] or <DM1.PrintQ."field"> (case-insensitive dataset).
const FIELD_REF = /[[<]\s*[A-Za-z0-9_]+\.[A-Za-z0-9_]+\.\s*"([^"]+)"\s*[\]>]/g;

/** Exact fr3-field → classic-token matches. */
const EXACT_MAP = {
    BARCODE: 'BARCODE',
    VP: 'PRICE',
    PRIJS: 'PRICE',
    PRICE: 'PRICE',
    SEIZOEN: 'SEASON',
    SEASON: 'SEASON',
    SIZE: 'SIZE',
    MAAT: 'SIZE',
    MTOMSX: 'SIZE',
    LEVART4: 'DETAIL4',
};

const COMPOSITE_SLOTS = ['TITLE', 'LINE1', 'LINE2', 'LINE3', 'LINE4'];

/**
 * Parse the fr3 XML text into a designer model.
 *
 * @param {string} xmlText  decoded .fr3 XML
 * @param {number} dpmm     target printer density (8 = 203 dpi)
 */
export function importFr3(xmlText, dpmm) {
    const d = dpmm || 8;
    const dpi = Math.round(d * 25.4);

    let doc;
    try {
        doc = new DOMParser().parseFromString(xmlText, 'text/xml');
    } catch (e) {
        return { ok: false, reason: 'XML parse failed' };
    }
    if (!doc || !doc.documentElement) return { ok: false, reason: 'XML parse failed' };
    if (doc.getElementsByTagName('parsererror').length > 0) {
        return { ok: false, reason: 'Not well-formed XML' };
    }

    const root = doc.documentElement;
    if (root.nodeName === 'inherited' || root.getAttribute('ParentReport')) {
        // Same refusal as the Android renderer: the parent form is invisible here.
        return { ok: false, reason: 'inherited' };
    }
    if (root.nodeName !== 'TfrxReport') {
        return { ok: false, reason: 'Not a FastReport .fr3 document' };
    }

    const model = { widthMm: 50, heightMm: 25, dpmm: d, elements: [] };
    const warnings = [];
    const fieldMap = {};       // fr3 field -> chosen token
    const usedSlots = [];

    const mapField = (rawField) => {
        const field = rawField.trim().toUpperCase();
        if (fieldMap[field]) return fieldMap[field];
        let token;
        if (EXACT_MAP[field]) {
            token = EXACT_MAP[field];
        } else {
            token = COMPOSITE_SLOTS.find((slot) => usedSlots.indexOf(slot) < 0);
            if (token) {
                usedSlots.push(token);
                warnings.push('[' + field + '] mapped to [' + token + '] — check the field assignment');
            } else {
                token = field;
                warnings.push('[' + field + '] has no classic token — the device will print it blank');
            }
        }
        fieldMap[field] = token;
        return token;
    };

    const convertText = (raw) => raw.replace(FIELD_REF, (m, f) => '[' + mapField(f) + ']');

    // Objects the template's script hides when the article has no barcode.
    const hideWithoutBarcode = {};
    const script = attr(root, 'ScriptText.Text', '');
    if (script) {
        const branch = script.match(/if\s*[<[][^>\]]*"barcode"[^>\]]*[>\]]\s*=\s*0\s*then([\s\S]*?)(?:\belse\b|\bend\s*;\s*end)/i);
        if (branch) {
            const hideRe = /([A-Za-z0-9_]+)\.visible\s*:=\s*false/gi;
            let hm = hideRe.exec(branch[1]);
            while (hm) {
                hideWithoutBarcode[hm[1]] = true;
                hm = hideRe.exec(branch[1]);
            }
        }
    }

    // Page geometry + margins (margins offset every element, like Android).
    let leftMarginUnits = 0;
    let topMarginUnits = 0;
    const pages = root.getElementsByTagName('TfrxReportPage');
    if (pages.length > 0) {
        const page = pages[0];
        const pw = attrD(page, 'PaperWidth', 0);
        const ph = attrD(page, 'PaperHeight', 0);
        if (pw > 0) model.widthMm = round2(pw);
        if (ph > 0) model.heightMm = round2(ph);
        leftMarginUnits = mmToUnits(attrD(page, 'LeftMargin', 0));
        topMarginUnits = mmToUnits(attrD(page, 'TopMargin', 0));
    }

    const BAND_TAGS = {
        TfrxMasterData: 1, TfrxDetailData: 1, TfrxHeader: 1, TfrxFooter: 1,
        TfrxChild: 1, TfrxPageHeader: 1, TfrxPageFooter: 1, TfrxReportTitle: 1,
    };

    // Walk the tree keeping the enclosing band's origin (element coords are
    // band-relative, exactly like the Android converter).
    let converted = 0;

    const walk = (node, bandLeft, bandTop) => {
        for (let child = node.firstChild; child; child = child.nextSibling) {
            if (child.nodeType !== 1) continue;
            const tag = child.nodeName;

            if (BAND_TAGS[tag]) {
                walk(child, attrD(child, 'Left', 0), attrD(child, 'Top', 0));
                continue;
            }

            const originLeft = leftMarginUnits + bandLeft;
            const originTop = topMarginUnits + bandTop;
            const bcOnly = hideWithoutBarcode[attr(child, 'Name', '')] === true;

            if (tag === 'TfrxMemoView') {
                const rawText = attr(child, 'Text', '');
                if (rawText.trim() === '') { walk(child, bandLeft, bandTop); continue; }

                const left = attrD(child, 'Left', 0);
                const top = attrD(child, 'Top', 0);
                const width = attrD(child, 'Width', 0);
                const height = attrD(child, 'Height', 0);
                const fontUnits = Math.abs(attrD(child, 'Font.Height', -11));

                // vaCenter/vaBottom: bake the vertical bias into y (no ZPL vaCenter).
                let vOffset = 0;
                const vAlign = attr(child, 'VAlign', '');
                if (vAlign === 'vaCenter' && height > fontUnits) vOffset = (height - fontUnits) / 2.0;
                else if (vAlign === 'vaBottom' && height > fontUnits) vOffset = height - fontUnits;

                // An explicit memo fill masks what's beneath (digits over bars):
                // reproduce as a filled white box UNDER the text element.
                if (attr(child, 'Color', '') !== '') {
                    model.elements.push({
                        id: nextId(), type: 'box',
                        x: unitsToMm(originLeft + left), y: unitsToMm(originTop + top),
                        w: unitsToMm(width), h: unitsToMm(height),
                        thickness: 1, fill: 'white', bcOnly,
                    });
                }

                const hAlign = attr(child, 'HAlign', 'haLeft');
                model.elements.push({
                    id: nextId(), type: 'text',
                    x: unitsToMm(originLeft + left),
                    y: unitsToMm(originTop + top + vOffset),
                    w: unitsToMm(width), h: unitsToMm(height),
                    text: convertText(rawText),
                    font: Math.max(6, unitsToDots(fontUnits, dpi)),
                    fontW: Math.max(6, unitsToDots(fontUnits, dpi)),
                    align: hAlign === 'haCenter' ? 'C' : (hAlign === 'haRight' ? 'R' : (hAlign === 'haBlock' ? 'J' : 'L')),
                    maxLines: 3,
                    rotation: 'N',
                    bcOnly,
                });
                converted += 1;
            } else if (tag === 'TfrxBarCodeView') {
                const left = attrD(child, 'Left', 0);
                const top = attrD(child, 'Top', 0);
                const width = attrD(child, 'Width', 0);
                const height = attrD(child, 'Height', 0);
                const barType = attr(child, 'BarType', 'bcCode128');
                const showText = String(attr(child, 'ShowText', 'True')).toLowerCase() !== 'false';
                const rotation = Math.round(attrD(child, 'Rotation', 0)) % 360;

                let type = 'CODE128';
                let modules = 0;
                if (barType.indexOf('EAN13') >= 0) { type = 'EAN13'; modules = 95; }
                else if (barType.indexOf('EAN8') >= 0) { type = 'EAN8'; modules = 67; }
                else if (barType.indexOf('39') >= 0 || barType.indexOf('3of9') >= 0) { type = 'CODE39'; }

                const wDots = unitsToDots(width, dpi);
                let module = 2;
                if (modules > 0 && wDots > 0) {
                    module = Math.max(1, Math.min(4, Math.floor(wDots / modules)));
                }

                model.elements.push({
                    id: nextId(), type: 'barcode',
                    // +5 dots quiet-zone nudge, photo-calibrated in the Android converter.
                    x: round2(unitsToMm(originLeft + left) + 5 / d),
                    y: unitsToMm(originTop + top),
                    w: unitsToMm(width), h: unitsToMm(height),
                    data: '[BARCODE]',
                    barType: type,
                    interpretation: showText,
                    module,
                    rotation: rotation === 90 ? 'R' : (rotation === 180 ? 'I' : (rotation === 270 ? 'B' : 'N')),
                    bcOnly,
                });
                converted += 1;
            } else if (tag === 'TfrxShapeView') {
                model.elements.push({
                    id: nextId(), type: 'box',
                    x: unitsToMm(originLeft + attrD(child, 'Left', 0)),
                    y: unitsToMm(originTop + attrD(child, 'Top', 0)),
                    w: unitsToMm(attrD(child, 'Width', 0)),
                    h: unitsToMm(attrD(child, 'Height', 0)),
                    thickness: Math.max(1, unitsToDots(attrD(child, 'Frame.Width', 1), dpi)),
                    fill: 'none',
                    bcOnly,
                });
                converted += 1;
            } else if (tag === 'TfrxPictureView') {
                const w = attrD(child, 'Width', 0);
                const h = attrD(child, 'Height', 0);
                if (w > 0 && h > 0) {
                    model.elements.push({
                        id: nextId(), type: 'logo',
                        x: unitsToMm(originLeft + attrD(child, 'Left', 0)),
                        y: unitsToMm(originTop + attrD(child, 'Top', 0)),
                        w: unitsToMm(w), h: unitsToMm(h),
                        bcOnly,
                    });
                    converted += 1;
                }
            } else if (tag === 'TfrxLineView') {
                const w = attrD(child, 'Width', 0);
                const h = attrD(child, 'Height', 0);
                model.elements.push({
                    id: nextId(), type: 'box',
                    x: unitsToMm(originLeft + attrD(child, 'Left', 0)),
                    y: unitsToMm(originTop + attrD(child, 'Top', 0)),
                    w: unitsToMm(Math.max(w, 0.5)), h: unitsToMm(Math.max(h, 0.5)),
                    thickness: Math.max(1, unitsToDots(attrD(child, 'Frame.Width', 1), dpi)),
                    fill: 'none',
                    bcOnly,
                });
                converted += 1;
            } else {
                walk(child, bandLeft, bandTop);
            }
        }
    };

    walk(root, 0, 0);

    if (converted === 0) {
        return { ok: false, reason: 'No convertible objects found (memo/barcode/shape/picture)' };
    }

    // bcOnly is kept on the elements for reference, but the kasvar print path
    // never strips BCONLY markers — the compiler intentionally ignores it.
    return { ok: true, model, warnings };
}

/**
 * Decode raw .fr3 bytes (base64 from auth) honouring the XML prolog's
 * declared encoding — legacy templates can be windows-1252.
 */
export function decodeFr3Base64(b64) {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    // Sniff the declared encoding from the (ASCII) prolog.
    let prolog = '';
    for (let i = 0; i < Math.min(bytes.length, 200); i++) prolog += String.fromCharCode(bytes[i]);
    const m = prolog.match(/encoding="([^"]+)"/i);
    let enc = m ? m[1].toLowerCase() : 'utf-8';
    if (enc === 'windows-1252' || enc === 'cp1252' || enc === 'iso-8859-1') enc = 'windows-1252';
    else enc = 'utf-8';

    try {
        return new TextDecoder(enc).decode(bytes);
    } catch (e) {
        return new TextDecoder('utf-8').decode(bytes);
    }
}
